import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Save,
  ArrowLeft,
  Loader2,
  Layout,
  Image as ImageIcon,
  Type,
  Lightbulb,
  Package,
  Hash,
  XCircle,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

// Form Parts
import SortDataForm from "../../components/case-studies/SortDataForm";
import BannerForm from "../../components/case-studies/BannerForm";
import OverviewForm from "../../components/case-studies/OverviewForm";
import ThinkingForm from "../../components/case-studies/ThinkingForm";
import BuiltForm from "../../components/case-studies/BuiltForm";
import WebsiteInfoForm from "../../components/case-studies/WebsiteInfoForm";
import FAQForm from "../../components/case-studies/FAQForm";
import ImprovementForm from "../../components/case-studies/ImprovementForm";
import ImageUpload from "../../components/common/ImageUpload";

import useCaseStudies from "../../hooks/useCaseStudies";
import caseStudyService from "../../services/caseStudyService";

const CaseStudyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addCaseStudy, updateCaseStudy } = useCaseStudies();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(!!id);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    page_url: "",
    sort_data: {
      title: "",
      sub_title: "",
      image: "",
      tags: [],
      industry_id: [],
      revenue_goal_id: [],
      capability_id: [],
      business_problem_id: [],
    },
    banner: { image: "", title: "", sub_title: "", tools: [] },
    improvement_section: Array(3).fill({ name: "", value: "" }),
    overview: { description: "", tags: [], cards: [] },
    thinking: [],
    built: Array(5).fill({ title: "", description: "", image: "" }),
    progress_image: "",
    website_info: { title: "", sub_title: "", link: "", button_text: "" },
    que_ans: { title: "", sub_title: "", questions: [] },
  });

  useEffect(() => {
    if (id) {
      const loadData = async () => {
        try {
          const data = await caseStudyService.getCaseStudyById(id);
          if (data) {
            setFormData((prev) => ({
              ...prev,
              ...data,
              sort_data: {
                ...prev.sort_data,
                ...(data.sort_data || {}),
                tags: (data.sort_data && data.sort_data.tags) || [],
                industry_id: (data.sort_data && data.sort_data.industry_id) || [],
                revenue_goal_id: (data.sort_data && data.sort_data.revenue_goal_id) || [],
                capability_id: (data.sort_data && data.sort_data.capability_id) || [],
                business_problem_id: (data.sort_data && data.sort_data.business_problem_id) || [],
              },
              banner: {
                ...prev.banner,
                ...(data.banner || {}),
                tools: (data.banner && data.banner.tools) || [],
              },
              improvement_section: Array.isArray(data.improvement_section) 
                ? data.improvement_section 
                : prev.improvement_section,
              overview: {
                ...prev.overview,
                ...(data.overview || {}),
                tags: (data.overview && data.overview.tags) || [],
                cards: (data.overview && data.overview.cards) || [],
              },
              thinking: Array.isArray(data.thinking) ? data.thinking : prev.thinking,
              built: Array.isArray(data.built) ? data.built : prev.built,
              website_info: {
                ...prev.website_info,
                ...(data.website_info || {}),
              },
              que_ans: {
                ...prev.que_ans,
                ...(data.que_ans || {}),
                questions: (data.que_ans && data.que_ans.questions) || [],
              },
            }));
          } else {
            navigate("/case-studies");
          }
        } catch (error) {
          navigate("/case-studies");
        } finally {
          setIsLoading(false);
        }
      };
      loadData();
    }
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!formData.page_url || formData.page_url.trim() === "") {
      toast.error("Case Study URL Slug is required");
      return;
    }

    // Listing validation
    if (!formData.sort_data.title || !formData.sort_data.sub_title || !formData.sort_data.image) {
      toast.error("Listing Title, Subtitle, and Image are required");
      return;
    }

    // Banner validation
    if (!formData.banner.title || formData.banner.title.trim() === "") {
      toast.error("Banner Title is required");
      return;
    }
    if (!formData.banner.sub_title || formData.banner.sub_title.trim() === "") {
      toast.error("Banner Subtitle is required");
      return;
    }
    if (!formData.banner.image || formData.banner.image.trim() === "") {
      toast.error("Banner Image is required");
      return;
    }
    if (!formData.banner.tools || formData.banner.tools.length === 0) {
      toast.error("At least one Tech Stack Tool is required");
      return;
    }
    if (formData.banner.tools.some(tool => !tool || tool.trim() === "")) {
      toast.error("All Tech Stack Tool icons must be uploaded");
      return;
    }

    // Improvement Stats validation
    if (!formData.improvement_section || formData.improvement_section.length !== 3) {
      toast.error("Exactly 3 Improvement Stats are required");
      return;
    }
    if (formData.improvement_section.some(stat => !stat.name || stat.name.trim() === "" || !stat.value || stat.value.trim() === "")) {
      toast.error("All Improvement Stats must have a Metric Name and Value");
      return;
    }

    // Overview & Dynamic Cards validation
    if (!formData.overview.description || formData.overview.description.trim() === "") {
      toast.error("Overview Description is required");
      return;
    }
    if (!formData.overview.tags || formData.overview.tags.length === 0) {
      toast.error("Overview Tags are required");
      return;
    }
    if (!formData.overview.cards || formData.overview.cards.length !== 4) {
      toast.error("4 Dynamic Cards are required in the Overview section");
      return;
    }

    // Validate each card
    for (let i = 0; i < formData.overview.cards.length; i++) {
      const card = formData.overview.cards[i];
      const cardNum = i + 1;

      if (card.type === "image") {
        if (!card.image) {
          toast.error(`Image is required for Dynamic Card ${cardNum}`);
          return;
        }
      } else if (card.type === "data") {
        if (!card.title || card.title.trim() === "") {
          toast.error(`Title is required for Dynamic Card ${cardNum}`);
          return;
        }
        if (!card.sub_title || card.sub_title.trim() === "") {
          toast.error(`Subtitle is required for Dynamic Card ${cardNum}`);
          return;
        }
        if (!card.data || card.data.length === 0) {
          toast.error(`At least one list item is required for Dynamic Card ${cardNum}`);
          return;
        }
        if (card.data.some(d => !d || d.trim() === "")) {
          toast.error(`All list items must be filled for Dynamic Card ${cardNum}`);
          return;
        }
      } else if (card.type === "imageWithText") {
        if (!card.image) {
          toast.error(`Image is required for Dynamic Card ${cardNum}`);
          return;
        }
        if (!card.title || card.title.trim() === "") {
          toast.error(`Title is required for Dynamic Card ${cardNum}`);
          return;
        }
      }
    }

    // Thinking Points validation
    if (!formData.thinking || formData.thinking.length === 0) {
      toast.error("At least one Thinking Point is required");
      return;
    }
    if (formData.thinking.some(tp => !tp.title || tp.title.trim() === "" || !tp.description || tp.description.trim() === "")) {
      toast.error("All Thinking Points must have a Title and Description");
      return;
    }

    // Built Section validation
    if (!formData.built || formData.built.length !== 5) {
      toast.error("Exactly 5 steps are required in the Built Section");
      return;
    }
    if (formData.built.some((step, index) => !step.title || step.title.trim() === "" || !step.description || step.description.trim() === "" || !step.image)) {
      toast.error("All 5 steps in the Built Section must have a Title, Description, and Image");
      return;
    }

    // Progress Image validation
    if (!formData.progress_image || formData.progress_image.trim() === "") {
      toast.error("Progress section image is required");
      return;
    }

    // Website Info validation
    if (!formData.website_info.title || !formData.website_info.sub_title || !formData.website_info.link || !formData.website_info.button_text) {
      toast.error("All Website Information fields are required");
      return;
    }

    // FAQ validation
    if (!formData.que_ans.title) {
      toast.error("FAQ Section Title is required");
      return;
    }
    if (!formData.que_ans.questions || formData.que_ans.questions.length === 0) {
      toast.error("At least one FAQ Question & Answer is required");
      return;
    }
    if (formData.que_ans.questions.some(q => !q.title || q.title.trim() === "" || !q.description || q.description.trim() === "")) {
      toast.error("All FAQ Questions and Answers must be filled");
      return;
    }

    setIsSubmitting(true);

    let success;
    if (id) {
      success = await updateCaseStudy(id, formData);
    } else {
      success = await addCaseStudy(formData);
    }

    if (success) navigate("/case-studies");
    setIsSubmitting(false);
  };

  const updateSection = React.useCallback((section, dataOrFn) => {
    setFormData((prev) => ({
      ...prev,
      [section]: typeof dataOrFn === "function" ? dataOrFn(prev[section]) : dataOrFn,
    }));
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin text-primary mb-4" size={40} />
        <p className="text-slate-500 font-medium font-serif">
          Loading Case Study Editor...
        </p>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-slate-50 min-h-screen">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/case-studies")}
            className="rounded-full h-10 w-10 p-0 hover:bg-slate-100"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-serif">
              {id ? "Edit Case Study" : "Build New Case Study"}
            </h1>
            <p className="text-secondary/60 text-sm font-medium">
              Craft the story of your project success.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/case-studies")}
            disabled={isSubmitting}
            className="rounded-full px-6 font-semibold border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            <XCircle size={18} className="mr-2" /> Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-secondary text-white rounded-full px-8 gap-2 shadow-lg shadow-black/10 transition-all hover:scale-105 active:scale-95"
          >
            {isSubmitting ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <Save size={18} />
            )}
            {id ? "Save Changes" : "Publish Story"}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className=" space-y-12 p-6">

        {/* SECTION 1: Listing Detail */}
        <section className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-8 py-5 border-b border-slate-100 flex items-center gap-3">
            <Layout className="text-primary" size={24} />
            <h2 className="text-xl font-bold text-slate-800">Listing Detail</h2>
          </div>
          <div className="p-8">
            <SortDataForm
              data={formData.sort_data}
              submitted={submitted}
              onChange={(dataOrFn) => updateSection("sort_data", dataOrFn)}
              onTagsChange={(tags) =>
                setFormData(prev => ({
                  ...prev,
                  sort_data: { ...prev.sort_data, tags }
                }))
              }
            />
          </div>
        </section>

        {/* SECTION 2: Case Study Details */}
        <section className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-8 py-5 border-b border-slate-100 flex items-center gap-3">
            <Package className="text-primary" size={24} />
            <h2 className="text-xl font-bold text-slate-800">Case Study Details</h2>
          </div>

          <div className="p-8 space-y-12">
            {/* Case Study URL Slug & Priority */}
            <div className="bg-primary/5 rounded-2xl p-6 border border-primary/10">
              <div className="flex items-center gap-2 mb-6">
                <Hash className="text-primary" size={20} />
                <h3 className="text-lg font-bold">Configuration</h3>
              </div>
              <div className="space-y-2 max-w-md">
                <Label htmlFor="page_url" className="text-sm font-semibold">
                  Case Study URL Slug <span className="text-red-500">*</span>
                </Label>
                <div className="flex items-center gap-2 w-full">
                  <span className="text-slate-400 font-mono text-sm hidden lg:inline">
                    /case-studies/
                  </span>
                  <Input
                    id="page_url"
                    value={formData.page_url}
                    className={`bg-white ${(!formData.page_url && submitted) ? "border-red-500 focus-visible:ring-red-500 ring-1 ring-red-500" : ""}`}
                    onChange={(e) => {
                      const slug = e.target.value
                        .toLowerCase()
                        .replace(/\s+/g, "-")
                        .replace(/[^a-z0-9-]/g, "");
                      setFormData((prev) => ({ ...prev, page_url: slug }));
                    }}
                    placeholder="e.g. digital-transformation-v1"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Must be unique. No spaces. Use dashes.
                </p>
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* Banner Section */}
            <BannerForm
              data={formData.banner}
              submitted={submitted}
              onChange={(dataOrFn) => updateSection("banner", dataOrFn)}
            />

            <hr className="border-slate-100" />

            {/* Improvement Stats */}
            <ImprovementForm
              data={formData.improvement_section}
              submitted={submitted}
              onChange={(dataOrFn) => updateSection("improvement_section", dataOrFn)}
            />

            <hr className="border-slate-100" />

            {/* Overview Section */}
            <OverviewForm
              data={formData.overview}
              submitted={submitted}
              onChange={(dataOrFn) => updateSection("overview", dataOrFn)}
            />

            <hr className="border-slate-100" />

            {/* Thinking Points */}
            <ThinkingForm
              data={formData.thinking}
              submitted={submitted}
              onChange={(dataOrFn) => updateSection("thinking", dataOrFn)}
            />

            <hr className="border-slate-100" />

            {/* Built Section */}
            <BuiltForm
              data={formData.built}
              submitted={submitted}
              onChange={(dataOrFn) => updateSection("built", dataOrFn)}
            />

            <hr className="border-slate-100" />

            {/* Progress Image Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="text-primary" size={20} />
                <h3 className="text-lg font-bold">Progress Transformation Image</h3>
              </div>
              <div className={(!formData.progress_image && submitted) ? "rounded-lg border-2 border-red-500 p-1" : ""}>
                <ImageUpload
                  label={<span>Progress Section Image <span className="font-normal text-xs text-gray-400">(Full Width Image) <span className="text-red-500">*</span></span></span>}
                  value={formData.progress_image}
                  onChange={(url) => setFormData(prev => ({ ...prev, progress_image: url }))}
                />
              </div>
              {!formData.progress_image && submitted && (
                <p className="text-xs text-red-500">Progress section image is required.</p>
              )}
            </div>

            <hr className="border-slate-100" />

            {/* CTA / Website Info */}
            <WebsiteInfoForm
              data={formData.website_info}
              submitted={submitted}
              onChange={(dataOrFn) => updateSection("website_info", dataOrFn)}
            />

            <hr className="border-slate-100" />

            {/* FAQ */}
            <FAQForm
              data={formData.que_ans}
              submitted={submitted}
              onChange={(dataOrFn) => updateSection("que_ans", dataOrFn)}
            />
          </div>
        </section>

      </form>
    </div>
  );
};

export default CaseStudyForm;
